import React, { useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Avatar,
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  TextField,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  LocationOn,
  Person,
  ArrowForward,
  Dangerous,
  Cancel,
  DriveFileRenameOutline,
} from "@mui/icons-material";
import { useFirestoreTimestamp } from "../../../hooks/use-formatted-date";
import StatusDialog from "./StatusDialog";
import { useBoolean } from "../../../hooks/use-boolean";
import { endpoints } from "../../../utils/endpoints";
import axios from "axios";
import InitiateMovementEditFormDialog from "./InitiateMovementEditFormDialog";
import { useAuth } from "../../../contexts/AuthContext";
import { useSnackbar } from "notistack";

const MovementCard = ({ movement, refetch }) => {
  const { role, user, isAdmin } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const isOpenDialog = useBoolean();

  const isOpenEditFormDialog = useBoolean();

  const [comment, setComment] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const createdAtFormatted = useFirestoreTimestamp(movement?.createdAt, {
    format: "DD-MM-YYYY HH:mm:ss",
  })?.formattedDate;

  const updatedAtFormatted = useFirestoreTimestamp(movement?.updatedAt, {
    format: "DD-MM-YYYY HH:mm:ss",
  })?.formattedDate;

  const getStatusColor = (status) => {
    switch (status) {
      case "received":
        return "success";
      case "cancelled":
        return "error";
      case "rejected":
        return "warning";
      default:
        return "info";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "received":
        return <CheckCircle />;
      case "cancelled":
        return <Cancel />;
      case "rejected":
        return <Dangerous />;
      default:
        return <Schedule />;
    }
  };

  const [status, setStatus] = useState(null);

  const handleOpen = (status) => {
    setStatus(status);
    isOpenDialog.onTrue();
  };

  const handleConfirm = async () => {
    if (!status || !movement?.id) return;

    try {
      setIsLoading(true);

      if (!user?.email) {
        throw new Error("Unauthorized");
      }

      const payload = {
        batteryId: movement?.batteryId,
        status: {
          value: status,
          changedBy: user?.email,
        },
      };

      if (comment) payload.comment = comment;

      await axios.patch(
        `${endpoints.battery.updateBatteryMovementStatus}/${movement?.id}`,
        payload
      );

      await refetch();
      enqueueSnackbar("Status updated successfully!", { variant: "success" });
    } catch (error) {
      console.error("Error updating status:", error);
      enqueueSnackbar(error.message || "Error updating status", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      isOpenDialog.onFalse();
      setStatus(null);
    }
  };

  return (
    <>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    bgcolor: `${getStatusColor(movement?.status?.value)}.main`,
                    width: 32,
                    height: 32,
                    fontSize: "1.2rem",
                  }}
                >
                  {getStatusIcon(movement?.status?.value)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{movement.assetName}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {movement?.batteryId}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
              >
                <Chip
                  label={
                    movement?.status?.value === "received"
                      ? "Received"
                      : movement?.status?.value === "cancelled"
                      ? "Cancelled"
                      : movement?.status?.value === "rejected"
                      ? "Rejected"
                      : "In Transit"
                  }
                  color={getStatusColor(movement?.status?.value)}
                  size="small"
                />
              </Stack>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2">
                {movement?.state?.from?.value}
              </Typography>
              <ArrowForward fontSize="small" color="action" />
              <Typography variant="body2">
                {movement?.state?.to?.value}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">
                  Initiated by: {movement.initiatedBy}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Schedule fontSize="small" color="action" />
                <Typography variant="body2">
                  Initiated: {createdAtFormatted}
                </Typography>
              </Stack>
              {createdAtFormatted !== updatedAtFormatted && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Schedule fontSize="small" color="action" />
                  <Typography variant="body2">
                    Last Update: {updatedAtFormatted}
                  </Typography>
                </Stack>
              )}

              {movement?.status?.value === "received" && (
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">
                      Received by: {movement?.status?.changedBy}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2">
                      Received: {updatedAtFormatted}
                    </Typography>
                  </Stack>
                </>
              )}

              {movement?.status?.value === "cancelled" && (
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">
                      Cancelled by: {movement?.status?.changedBy}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2">
                      Cancelled: {updatedAtFormatted}
                    </Typography>
                  </Stack>
                </>
              )}

              {movement?.status?.value === "rejected" && (
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">
                      Rejected by: {movement?.status?.changedBy}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2">
                      Rejected: {updatedAtFormatted}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>

            {movement.remark && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="body2">
                  <strong>Remark:</strong> {movement.remark}
                </Typography>
              </Paper>
            )}

            {movement?.status?.value === "in-transit" && (
              <TextField
                fullWidth
                label="Add Comment (Optional)"
                multiline
                rows={2}
                variant="outlined"
                InputLabelProps={{
                  sx: {
                    fontSize: "0.9rem",
                  },
                }}
                onChange={(e) => setComment(e.target.value)}
                // value={movement?.comment || comment}
                placeholder="Add any additional information here..."
              />
            )}
            {movement?.status?.value === "in-transit" && (
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => handleOpen("received")}
                >
                  Receive
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<Dangerous />}
                  onClick={() => handleOpen("rejected")}
                >
                  Reject
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleOpen("cancelled")}
                >
                  Cancel
                </Button>
                {isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<DriveFileRenameOutline />}
                    onClick={isOpenEditFormDialog.onTrue}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            )}

            {movement.comment && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="body2">
                  <strong>Comment:</strong> {movement.comment}
                </Typography>
              </Paper>
            )}

            {movement?.status?.value !== "in-transit" && role === "admin" && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<DriveFileRenameOutline />}
                onClick={isOpenEditFormDialog.onTrue}
                sx={{ maxWidth: 80 }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
      <StatusDialog
        open={isOpenDialog.value}
        onClose={isOpenDialog.onFalse}
        onConfirm={handleConfirm}
        status={status}
        itemId={movement?.batteryId}
        isLoading={isLoading}
      />
      <InitiateMovementEditFormDialog
        open={isOpenEditFormDialog.value}
        onClose={isOpenEditFormDialog.onFalse}
        movementData={movement}
        refetch={refetch}
      />
    </>
  );
};

export default MovementCard;
